import { isCaseSettled, isWorkupAboveIfmv, isAssignmentComplete } from '../../SummaryCaseTable/utils/helpers.js';
import { getMostRecentOfferValue } from '../../../../utils/mostRecentOfferUtils.js';
import logger from '../../../../utils/logger';

/**
 * This is only really used for the finalization we updated the rest to use our email factory
 **/

/**
 * Validate basic parameters required for sending offers
 */
export const validateBasicParameters = (options) => {
  const { selectedRows, allRows, negotiationObj, user } = options;

  if (!negotiationObj || !negotiationObj.cases) {
    return {
      isValid: false,
      error: 'No negotiation data available'
    };
  }

  if (!selectedRows || selectedRows.length === 0) {
    return {
      isValid: false,
      error: 'No cases selected to process'
    };
  }

  if (!user) {
    return {
      isValid: false,
      error: 'User information is required'
    };
  }

  if (user?.userAccess?.level === 'Training') {
    return {
      isValid: false,
      error: 'Training users cannot send email offers. This action is restricted.'
    };
  }

  return {
    isValid: true,
    error: null
  };
};

/**
 * Validate that cases have offer values
 */
export const validateCasesHaveOffers = (casesToCheck) => {
  if (!casesToCheck || casesToCheck.length === 0) {
    return {
      isValid: false,
      error: 'No cases provided for validation',
      missingOffers: []
    };
  }

  // Filter out settled cases - they don't need offer validation
  const unsettledCases = casesToCheck.filter(caseItem => !isCaseSettled(caseItem));
  
  // Check for missing offer values in unsettled cases
  const missingOffers = unsettledCases.filter(caseItem => {
    if (!caseItem) return false;
    
    const hasOfferValue = caseItem.OfferValue !== null && 
                         caseItem.OfferValue !== 'None' && 
                         caseItem.OfferValue !== '' && 
                         caseItem.OfferValue !== 0 &&
                         !isNaN(Number(caseItem.OfferValue)) &&
                         Number(caseItem.OfferValue) > 0;
    
    return !hasOfferValue;
  });

  const isValid = missingOffers.length === 0;

  return {
    isValid,
    missingOffers,
    error: isValid ? null : `Missing offers for ${missingOffers.length} case(s)`
  };
};

/**
 * Validate that all cases are finalized (CaseReview flag set)
 */
export const validateCasesAreFinalized = (casesToCheck, negotiationObj) => {
  if (!casesToCheck || casesToCheck.length === 0) {
    return {
      isValid: false,
      error: 'No cases provided for validation',
      unfinalizedCases: []
    };
  }

  // Filter out settled cases - they don't need finalization validation
  const unsettledCases = casesToCheck.filter(caseItem => !isCaseSettled(caseItem));
  
  // Check if cases are finalized
  const unfinalizedCases = unsettledCases.filter(caseItem => {
    const caseFromState = negotiationObj.cases.find(c => c.parcel_id === caseItem.parcel_id);
    const isFinalized = isAssignmentComplete(caseFromState, 'CaseReview');
    return !isFinalized;
  });

  const isValid = unfinalizedCases.length === 0;

  return {
    isValid,
    unfinalizedCases,
    error: isValid ? null : `${unfinalizedCases.length} case(s) are not finalized. They must be finalized before sending.`
  };
};

/**
 * Validate that cases have workup values (ProprietyWorkup)
 */
export const validateCasesHaveWorkupValues = (casesToCheck) => {
  if (!casesToCheck || casesToCheck.length === 0) {
    return {
      isValid: true,
      missingWorkupValues: []
    };
  }

  // Filter out settled cases - they don't need workup validation
  const unsettledCases = casesToCheck.filter(caseItem => !isCaseSettled(caseItem));
  logger.log('the CASES COMING IN:', casesToCheck);
  
  // Check for missing workup values
  const missingWorkupValues = unsettledCases.filter(caseItem => {
    if (!caseItem) return false;

    const rawWorkup = caseItem.ProprietyWorkup;
    const hasDirectWorkup =
      rawWorkup !== null &&
      rawWorkup !== undefined &&
      rawWorkup !== '' &&
      !Number.isNaN(Number(rawWorkup)) &&
      Number(rawWorkup) > 0;

    const fallbackWorkup = hasDirectWorkup
      ? null
      : getMostRecentOfferValue(caseItem.Negotiations || [], 'Aventine');

    const effectiveWorkup = hasDirectWorkup ? Number(rawWorkup) : Number(fallbackWorkup);

    const hasWorkupValue =
      !Number.isNaN(effectiveWorkup) &&
      effectiveWorkup > 0;
    
    return !hasWorkupValue;
  });

  const isValid = missingWorkupValues.length === 0;

  return {
    isValid,
    missingWorkupValues,
    error: isValid ? null : `Missing workup values for ${missingWorkupValues.length} case(s)`
  };
};

/**
 * Check if user is selecting a subset of available cases
 */
export const checkForSubsetWarning = (selectedRows, allRows) => {
  if (!selectedRows || selectedRows.length === 0) {
    return { 
      hasSubsetWarning: false, 
      openCasesNotSelected: [] 
    };
  }
  
  // Filter to only open (unsettled) cases
  const openRows = allRows.filter(row => !isCaseSettled(row));
  
  // If user selected all open cases, no warning needed
  if (selectedRows.length === openRows.length) {
    return { 
      hasSubsetWarning: false, 
      openCasesNotSelected: [] 
    };
  }
  
  // Find open cases that weren't selected
  const selectedParcelIds = selectedRows.map(row => row.parcel_id);
  const openCasesNotSelected = openRows.filter(row => !selectedParcelIds.includes(row.parcel_id));
  
  return {
    hasSubsetWarning: openCasesNotSelected.length > 0,
    openCasesNotSelected: openCasesNotSelected
  };
};

export const getWorkupsAboveIfmv = (casesToCheck) =>
  (casesToCheck || []).filter((caseItem) => !isCaseSettled(caseItem) && isWorkupAboveIfmv(caseItem));

/**
 * Check if ALL selected cases are attempting to be sent but not all are finalized
 */
export const validateSendAllCases = (selectedRows, allRows, negotiationObj) => {
  // Get unsettled cases from allRows
  const unsettledAllRows = allRows.filter(row => !isCaseSettled(row));
  
  // Check if user is trying to send all cases
  if (selectedRows.length !== unsettledAllRows.length) {
    // Not sending all cases, this validation doesn't apply
    return {
      isValid: true,
      error: null
    };
  }
  
  // User is sending all cases - check if all are finalized
  const finalizedCases = selectedRows.filter(row => {
    const caseFromState = negotiationObj.cases.find(c => c.parcel_id === row.parcel_id);
    const isFinalized = isAssignmentComplete(caseFromState, 'CaseReview');
    return isFinalized;
  });
  
  if (finalizedCases.length !== selectedRows.length) {
    const unfinalizedCount = selectedRows.length - finalizedCases.length;
    return {
      isValid: false,
      error: `Not all cases are finalized. ${unfinalizedCount} case(s) still need to be finalized before sending.`,
      unfinalizedCount
    };
  }
  
  return {
    isValid: true,
    error: null
  };
};

/**
 * Run all validations for offer email sending
 * @param {Object} options - Validation options
 * @param {Array} options.selectedRows - Selected rows to validate
 * @param {Array} options.allRows - All available rows
 * @param {Object} options.negotiationObj - Negotiation object
 * @param {Object} options.user - User object
 * @param {boolean} options.skipSubsetCheck - Skip subset warning check
 * @returns {Object} Complete validation result
 */
export const validateOfferEmailWorkflow = (options) => {
  const {
    selectedRows,
    allRows,
    negotiationObj,
    user,
    skipSubsetCheck = false,
    skipWorkupsAboveIfmvCheck = false,
  } = options;

  logger.log('🔍 [OFFER EMAIL VALIDATION] Running all validations:', {
    selectedRowsCount: selectedRows?.length,
    allRowsCount: allRows?.length,
    skipSubsetCheck,
    villageRun: negotiationObj?.villageRun
  });

  // Step 1: Validate basic parameters
  const basicValidation = validateBasicParameters({
    selectedRows,
    allRows,
    negotiationObj,
    user
  });

  if (!basicValidation.isValid) {
    return {
      isValid: false,
      error: basicValidation.error,
      validationType: 'basic_parameters'
    };
  }

  // Step 2: Ensure user selected at least one unsettled case
  const unsettledSelectedCases = (selectedRows || []).filter((row) => !isCaseSettled(row));
  if (unsettledSelectedCases.length === 0) {
    return {
      isValid: false,
      error: 'No unsettled cases selected',
      validationType: 'no_unsettled_cases_selected'
    };
  }

  // Step 3: Validate missing comps on unsettled cases only
  const missingCompsCases = unsettledSelectedCases.filter((caseItem) => {
    const v = caseItem?.ProprietyWorkup;
    return v === null || v === undefined || v === '' || Number.isNaN(Number(v)) || Number(v) <= 0;
  });

  if (missingCompsCases.length > 0) {
    return {
      isValid: false,
      error: 'One or more selected parcels do not have comps set.',
      validationType: 'missing_comps',
      missingCompsCases
    };
  }

  // Step 4: Validate send all cases (if applicable)
  const sendAllValidation = validateSendAllCases(selectedRows, allRows, negotiationObj);

  if (!sendAllValidation.isValid) {
    return {
      isValid: false,
      error: sendAllValidation.error,
      validationType: 'send_all_not_finalized',
      unfinalizedCount: sendAllValidation.unfinalizedCount
    };
  }

  // Step 5: Validate cases have offers
  const offerValidation = validateCasesHaveOffers(selectedRows);
  
  if (!offerValidation.isValid) {
    return {
      isValid: false,
      error: offerValidation.error,
      validationType: 'missing_offers',
      missingOffers: offerValidation.missingOffers
    };
  }

  // Step 6: Validate cases are finalized
  const finalizedValidation = validateCasesAreFinalized(selectedRows, negotiationObj);

  if (!finalizedValidation.isValid) {
    return {
      isValid: false,
      error: finalizedValidation.error,
      validationType: 'not_finalized',
      unfinalizedCases: finalizedValidation.unfinalizedCases
    };
  }

  // Step 7: Check for subset warning (if not skipped)
  if (!skipSubsetCheck) {
    const subsetCheck = checkForSubsetWarning(selectedRows, allRows);
    
    if (subsetCheck.hasSubsetWarning) {
      return {
        isValid: false,
        error: 'You are sending a subset of cases. Other open cases will not receive offers.',
        validationType: 'subset_warning',
        openCasesNotSelected: subsetCheck.openCasesNotSelected
      };
    }
  }

  if (!skipWorkupsAboveIfmvCheck) {
    const workupsAboveIfmv = getWorkupsAboveIfmv(selectedRows);
    if (workupsAboveIfmv.length > 0) {
      return {
        isValid: false,
        error: `${workupsAboveIfmv.length} workup(s) are above IFMV and will be omitted from town display if not updated.`,
        validationType: 'workups_above_ifmv',
        workupsAboveIfmv,
      };
    }
  }

  // All validations passed
  logger.log('✅ [OFFER EMAIL VALIDATION] All validations passed');
  
  return {
    isValid: true,
    error: null,
    validationType: null
  };
};
